import * as React from 'react';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { UserService } from '@enginuity-common/services';
import { IRegistrationData } from '@enginuity-common/types';
import RegisterForm, { IRegistrationFormData } from '../../components/registration/register-form';
import EngageOnboardForm, { IEngageOnboardingFormData } from '../../components/onboarding/engage-onboard-form';
import CheckEmail from '../../components/misc/check-email';
import { SpinnerOverlay } from '@enginuity-apps-ui-components/atoms';
import GenericErrorMessage from '../../components/misc/generic-error-message';
import { getUrlParameter } from '@enginuity-common/utils/url';
import { useHistory } from "react-router-dom";

enum RegistrationFormState {
  registering,
  onboarding,
  complete,
  error
};

export type IRegisterProps = {
  fromUri: string;
};

const Register: React.FunctionComponent<IRegisterProps> = ({fromUri}) => {
  const [registrationState, setRegistrationState] = useState(RegistrationFormState.registering);
  const [registrationFormData, setRegistrationFormData] = useState<IRegistrationFormData>();
  const [genericError, setGenericError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<JSX.Element | undefined>();
  const [showSpinner, setShowSpinner] = useState(false);
  const history = useHistory();

  const usersService = new UserService({
    baseUrl: window.spConfig.usersBaseUrl
  });

  const onRegistrationSubmit = async (data: IRegistrationFormData) => {
    setRegistrationFormData(data);
    history.push("/register" + (fromUri && `?fromURI=${fromUri}`));
    setRegistrationState(RegistrationFormState.onboarding);
  };

  const OnOnBoardingBack = async () => {
    setRegistrationState(RegistrationFormState.registering);
  };

  const onEngageOnboardSubmit = async (data: IEngageOnboardingFormData) => {
    setSubmitError(undefined);
    setShowSpinner(true);
    if (data.employmentStatus === "Unemployed") {
      data.employer = "";
    }

    const completeRegistrationData: IRegistrationData = {
      firstName: registrationFormData?.firstName,
      lastName: registrationFormData?.lastName,
      email: registrationFormData?.email,
      terms: registrationFormData?.terms,
      keepMeInformed: registrationFormData?.keepMeInformed,
      employer: data.employer,
      employmentStatus: data.employmentStatus,
      qualification: data.qualification?.value,
      ageRange: data.ageRange?.value,
      sector: data.sector?.value,
      groupIds: `${window.spConfig.engageOktaGroupId},${window.spConfig.onboardingOktaGroupId}`,
      redirectUrl: fromUri || undefined,
    };

    setRegistrationFormData(completeRegistrationData);

    try {
      const registrationResponse = await usersService.register(completeRegistrationData);
      if (!registrationResponse.ok) {
        switch (registrationResponse.status) {
          case 409:
            setSubmitError(<>An account with this email already exists. Try <a href="/signin">signing in</a></>);
            setShowSpinner(false);
            return;

          default:
            throw new Error('Unsuccessful response from registration call');
        }
      }
    } catch (error) {
      console.error(error);
      setRegistrationState(RegistrationFormState.error);
      setGenericError(error);
      setShowSpinner(false);
      return;
    }

    setShowSpinner(false);
    setRegistrationState(RegistrationFormState.complete);
  };

  switch (registrationState) {
    case RegistrationFormState.registering:
      return (
        <div className="container  ">
          <div className="row md-spacer-80 spacer-32">
            <div className="col-lg-1"></div>
            <div className="col-lg-11">
              <h1>Create account</h1>
            </div>
          </div>
          <RegisterForm onSubmit={onRegistrationSubmit} data={registrationFormData} fromUri={fromUri} />
        </div>
      );

    case RegistrationFormState.onboarding:

      return (
        <div className="container  ">
          <div className="row md-spacer-80 spacer-32">
            <div className="col-lg-1"></div>
            <div className="col-lg-11">
              <h1>Create account</h1>
            </div>
          </div>
          <EngageOnboardForm onSubmit={onEngageOnboardSubmit} submitError={submitError} onBack={OnOnBoardingBack} startNumber={3} />
          <SpinnerOverlay active={showSpinner} />
        </div>
      );

    case RegistrationFormState.complete:
      return (
        <CheckEmail email={registrationFormData?.email} fromUri={fromUri}><p>Click the link in the email to setup your password</p></CheckEmail>
      );
    case RegistrationFormState.error:
      return (
        <GenericErrorMessage errorMsg={genericError} tryAgainLink="/register" fromUri={fromUri}></GenericErrorMessage>
      );
  }
};

export default Register;
